import { DateTime } from "@/Common/Helpers/DateTime";
import { MyleObjectType } from "@/Common/Models/Enum/MyleObjectType";
import { PortalLinkedItemType } from "@/Common/Models/Portal/Enums/PortalLinkedItemType";
import { SharedItem } from "@/Common/Models/Portal/SharedItem/SharedItem";
import { IPortalShareableItem } from "@/Common/Models/Portal/UI/IPortalShareableItem";
import { BasePortalShareableItem } from "@/Common/Models/ShareItems/BasePortalShareableItem";
import { SignedPrintAuthorization } from "@/Common/Models/SignedPrintAuthorization";
import { PatientForms } from "@/Patient/Models/Forms/RadPdf/Forms";
import { RadPdfForm } from "@/Patient/Models/Forms/RadPdf/RadPdfForm";

class PdfFormInboxItem extends BasePortalShareableItem implements IPortalShareableItem {
    public Id: string;
    public CreationDate: string;
    public RadDocumentId: string;
    public DocumentTag: string;
    public FormName: string;
    public IsPersistant: boolean;
    public IsVisible?: boolean;
    public contextOpen: boolean;
    public EncounterId: string;
    public ViewedFromEncounterId: string;
    public SharedItemEncounterId: string; // used to set the encounter on which this item was shared the latest
    public SharedItemDate: DateTime;
    public SharedItemFileDate: DateTime;
    public LastUpdateDate: DateTime;
    public HasBeenSharedOnPreviousEncounter: boolean;
    public SignedPrintAuthorizations: SignedPrintAuthorization[];
    public PdfToken: string;
    public FormId: string | null;

    /**
     * The unique identifier for an attachment.
     *
     * This property is used exclusively in the frontend to manage
     * the `sentFaxItemId`. It is optional and may be undefined if
     * no attachment is associated with the current context.
     *
     * @type {string | undefined}
     */
    public attachmentId?: string;

    constructor(item?: Partial<PdfFormInboxItem>) {
        super(item);
        Object.assign(this, item);
    }

    public static fromPatientForms(item: PatientForms, viewedFromEncounterId: string): PdfFormInboxItem {
        if (!item)
            return null;

        let model = new PdfFormInboxItem();
        model.Id = item.Id;
        model.CreationDate = item.CreationDate;
        model.RadDocumentId = item.RadDocumentId;
        model.DocumentTag = item.DocumentTag;
        model.IsPersistant = item.IsPersistant;
        model.contextOpen = item.contextOpen;
        model.EncounterId = item.EncounterId;
        model.ViewedFromEncounterId = viewedFromEncounterId; // Null == latest form available
        model.FormName = item.FormName;
        model.SharedItem = item.SharedItem;
        model.SharedItemId = item.SharedItemId;
        model.SharedItemMessages = item.SharedItemMessages;
        model.SharedItemDate = item.SharedItemDate;
        model.SharedItemFileDate = item.SharedItemFileDate;
        model.LastUpdateDate = item.LastUpdateDate;
        model.HasBeenSharedOnPreviousEncounter = item.HasBeenSharedOnPreviousEncounter;
        model.SignedPrintAuthorizations = item.SignedPrintAuthorizations;
        model.PdfToken = item.PdfToken;
        model.FormId = item.FormId;

        return model;
    }

    public static fromRadPdfForm(item: RadPdfForm): PdfFormInboxItem {
        if (!item)
            return null;

        let model = new PdfFormInboxItem();
        model.Id = item.PatientFormId;
        model.RadDocumentId = item.RadDocumentId;
        model.DocumentTag = item.RadDocumentTag;
        model.EncounterId = item.EncounterId;
        model.ViewedFromEncounterId = item.EncounterId;
        model.FormName = item.FormName;
        model.LastUpdateDate = item.LastUpdateDate;
        model.FormId = item.FormId;

        return model;
    }

    public hasBeenModifiedSinceLastShared(): boolean {
        if (!this.LastUpdateDate)
            return false;

        // if shared on this note, check if the form has been updated since
        if (this.SharedItemDate) {
            return (this.SharedItemFileDate && this.SharedItemFileDate < this.LastUpdateDate)
                || this.SharedItemDate < this.LastUpdateDate;
        }

        // if shared on a previous note, check if the form has been updated since the note was created
        return this.HasBeenSharedOnPreviousEncounter && this.CreationDate < this.LastUpdateDate;
    }

    public hasBeenSharedOnPreviousEncounter(): boolean {
        return this.HasBeenSharedOnPreviousEncounter;
    }

    public getItemTypeHeader(): string {
        return "";
    }

    public getItemTitle(): string {
        return `${cacheStore.GetString("FormLabel")}: ${this.FormName}`
            .truncate(SharedItem.SubjectMaxLength);
    }

    public getDefaultSubject(): string {
        return this.FormName;
    }

    public getPortalObjectId(): string {
        return this.Id;
    }

    public getPortalLinkedItemType(): PortalLinkedItemType {
        return PortalLinkedItemType.MedicalNoteForm;
    }

    public getMyleObjectType(): MyleObjectType {
        return MyleObjectType.MedicalNoteForm;
    }

    public getPortalStatusTooltip(): string {
        if (this.hasBeenModifiedSinceLastShared()) {
            return cacheStore.GetString("PortalUnsynchronizedTooltipLabel");
        }
    }
}

export { PdfFormInboxItem };
